import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import Skeleton from "components/ui/Skeleton";
import apiApproval from "../api";
import { MODULE_TYPES } from "./module-config";

const ApprovalModuleCards = ({ onSelect }) => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCounts = async () => {
      setLoading(true);
      const results = await Promise.allSettled(
        MODULE_TYPES.map((module) =>
          Promise.all([
            apiApproval.getAll({ type: module.key, skip: 0, limit: 1 }),
            apiApproval.getAll({ type: module.key, status: "PENDING", skip: 0, limit: 1 }),
          ])
        )
      );

      if (cancelled) return;

      const next = {};
      results.forEach((result, index) => {
        const key = MODULE_TYPES[index].key;
        if (result.status === "fulfilled") {
          const [totalRes, pendingRes] = result.value;
          next[key] = {
            total: totalRes.data?.total_count || 0,
            pending: pendingRes.data?.total_count || 0,
          };
        } else {
          next[key] = { total: 0, pending: 0 };
        }
      });

      setCounts(next);
      setLoading(false);
    };

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <Skeleton.Tiles count={4} className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {MODULE_TYPES.map((module) => {
        const Icon = module.icon;
        const moduleCounts = counts[module.key] || { total: 0, pending: 0 };

        return (
          <button
            key={module.key}
            type="button"
            onClick={() => onSelect(module.key)}
            className="relative flex flex-col gap-4 overflow-hidden rounded-xl border border-border bg-surface p-5 text-left cursor-pointer"
          >
            <span className={`absolute inset-x-0 top-0 h-1 ${module.accent}`} />

            <div className="flex items-start justify-between gap-2">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${module.iconWrap}`}>
                <Icon size={20} />
              </div>
              <ChevronRight size={18} className="mt-1 shrink-0 text-text_muted" />
            </div>

            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-text_primary">{module.label}</div>
              <div className="mt-0.5 text-xs text-text_secondary">{module.description}</div>
            </div>

            <div className="flex items-center gap-2 border-t border-border pt-3">
              {moduleCounts.pending > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-warning_border bg-warning_bg px-2.5 py-1 text-[11px] font-semibold text-warning">
                  <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                  {moduleCounts.pending} pending
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-success_border bg-success_bg px-2.5 py-1 text-[11px] font-semibold text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  All clear
                </span>
              )}
              <span className="text-xs text-text_muted">{moduleCounts.total} total</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ApprovalModuleCards;
